import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  TablePagination,
  Chip,
  Snackbar,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  FilterAltOff,
  Description,
  CheckCircle,
  Pending,
  Search,
  Check,
  Cancel,
  Close
} from '@mui/icons-material';
import { contactClaimsAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManageContacts } from '../../utils/roleUtils';

const ClaimProvider = () => {
  const { t } = useLanguage();
  const adminUser = getAdminUser();
  const isSuperAdmin = canManageContacts(adminUser?.role);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [claims, setClaims] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Confirmation Dialog State
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [claimToProcess, setClaimToProcess] = useState(null);
  const [actionType, setActionType] = useState(''); // 'Approved' or 'Rejected'

  // Toast Notifications State
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });

  // Action Menu State (re-used for Proof Preview and status dialog)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');

  const handlePreviewOpen = (url) => {
    setPreviewUrl(url);
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setPreviewUrl('');
  };

  const debouncedSearchRef = useRef(debouncedSearch);

  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  // Debouncing search queries
  useEffect(() => {
    const id = window.setTimeout(() => {
      const next = (searchQuery || '').trim();
      const prev = debouncedSearchRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => window.clearTimeout(id);
  }, [searchQuery]);

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const fetchClaims = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await contactClaimsAPI.getContactClaims(page, rowsPerPage, debouncedSearch);
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const list = response.data.payload;
        setClaims(list);
        const apiTotal = response.data.totalRecord ?? response.data.totalCount;
        setTotalCount(
          apiTotal != null && Number(apiTotal) >= 0 ? Number(apiTotal) : list.length
        );
      } else {
        setClaims([]);
        setTotalCount(0);
        const apiMsg = response?.data?.errorMessage || response?.data?.message || 'Unknown error';
        showToast(`Failed to load claims: ${apiMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error fetching claims:', error);
      setClaims([]);
      setTotalCount(0);
      showToast(t('cinfo.loadFailed') || 'Failed to load provider claims.', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, t]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  const handleOpenConfirmDialog = (claim) => {
    setClaimToProcess(claim);
    setActionType('Approved'); // default to Approved
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setClaimToProcess(null);
    setActionType('');
    setConfirmDialogOpen(false);
  };

  const handleUpdateClaimStatus = async () => {
    if (!claimToProcess || !actionType) return;
    setIsSubmitting(true);
    const claimId = claimToProcess.claimId;
    try {
      const response = await contactClaimsAPI.updateClaimStatus(claimId, actionType);
      if (response?.data?.success === true) {
        showToast(
          actionType === 'Approved'
            ? t('providerClaims.toastApproved') || 'Claim approved successfully.'
            : t('providerClaims.toastRejected') || 'Claim rejected successfully.',
          'success'
        );
        fetchClaims();
      } else {
        const errMsg = response?.data?.errorMessage || response?.data?.message || 'Unknown error';
        showToast(`Failed to update claim status: ${errMsg}`, 'error');
      }
    } catch (error) {
      console.error('Error updating claim status:', error);
      const errMsg = error?.response?.data?.errorMessage || error?.message || 'Unknown error';
      showToast(`Error updating claim status: ${errMsg}`, 'error');
    } finally {
      setIsSubmitting(false);
      handleCloseConfirmDialog();
    }
  };

  const getStatusChipColor = (status) => {
    const s = String(status || '').toUpperCase();
    if (s === 'APPROVED') return 'success';
    if (s === 'REJECTED') return 'error';
    return 'warning'; // PENDING
  };

  return (
    <Box p={1}>
      {/* Title Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary" sx={{ fontWeight: 600 }}>
          {t('providerClaims.title') || 'Provider Claims Management'}
        </Typography>
      </Box>

      {/* Filter / Search Bar */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              label={t('common.search') || 'Search'}
              placeholder={t('providerClaims.searchPh') || 'Search claims by name or email...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'action.active', mr: 1, fontSize: 20 }} />,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#0F3D3E',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0F3D3E',
                  },
                },
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={() => {
                setSearchQuery('');
                setDebouncedSearch('');
              }}
              sx={clearFilterButtonSx}
            >
              {t('common.clear') || 'Clear'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Claims Table */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" my={6}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
            borderRadius: 3,
            border: '1px solid #f3f4f6',
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600,
              padding: '16px'
            },
            '& .MuiTableCell-body': {
              padding: '16px',
              color: '#374151'
            }
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('providerClaims.colContactTitle') || 'Contact Title'}</TableCell>
                <TableCell>{t('providerClaims.colFullName') || 'Full Name'}</TableCell>
                <TableCell>{t('providerClaims.colRole') || 'Role'}</TableCell>
                <TableCell>{t('providerClaims.colBusinessEmail') || 'Business Email'}</TableCell>
                <TableCell>{t('providerClaims.colPhoneNumber') || 'Phone Number'}</TableCell>
                <TableCell>{t('providerClaims.colProofOfOwnership') || 'Proof of Ownership'}</TableCell>
                <TableCell>{t('providerClaims.colStatus') || 'Status'}</TableCell>
                <TableCell align="center">{t('providerClaims.colActions') || 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {claims.map((claim) => (
                <TableRow
                  key={claim.claimId}
                  sx={{
                    transition: 'background-color 0.2s',
                    '&:hover': {
                      backgroundColor: '#f9fafb',
                    }
                  }}
                >
                  <TableCell style={{ fontWeight: 500 }}>
                    {claim.contactTitle || '-'}
                  </TableCell>
                  <TableCell>
                    {claim.fullName || '-'}
                  </TableCell>
                  <TableCell>{claim.role || '-'}</TableCell>
                  <TableCell>{claim.businessEmail || '-'}</TableCell>
                  <TableCell>{claim.phoneNumber || '-'}</TableCell>
                  <TableCell>
                    {claim.proofOfOwnershipUrl ? (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<Description />}
                        onClick={() => handlePreviewOpen(claim.proofOfOwnershipUrl)}
                        sx={{
                          color: '#0F3D3E',
                          textTransform: 'none',
                          fontWeight: 600,
                          '&:hover': { backgroundColor: '#f3e8ff' }
                        }}
                      >
                        View Proof
                      </Button>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                        Not Provided
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={claim.status || 'Pending'}
                      color={getStatusChipColor(claim.status)}
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {String(claim.status || '').toUpperCase() === 'PENDING' ? (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleOpenConfirmDialog(claim)}
                          sx={{
                            background: 'linear-gradient(135deg, #0F3D3E 0%, #0a2829 100%)',
                            color: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 0.3,
                            px: 1.2,
                            minHeight: 26,
                            fontSize: '0.75rem',
                            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
                            }
                          }}
                        >
                          {t('promotions.updateStatus') || 'Update Status'}
                        </Button>
                      </Box>
                    ) : (
                      <span>—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {claims.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ color: '#9ca3af', py: 6 }}>
                    {t('providerClaims.empty') || 'No Contact claims found.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Table Pagination */}
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage={t('common.rowsPerPage') || 'Rows per page:'}
            sx={{
              '.MuiTablePagination-select': { color: '#0F3D3E' },
              '.MuiTablePagination-selectIcon': { color: '#0F3D3E' },
              '.MuiTablePagination-displayedRows': { color: '#0F3D3E' },
              '.MuiTablePagination-actions': {
                '& .MuiIconButton-root': { color: '#0F3D3E' },
                '& .Mui-disabled': { color: '#e9d5ff' },
              }
            }}
          />
        </TableContainer>
      )}

      {/* Confirmation Process Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, p: 1 }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>
          {t('promotions.updateStatus') || 'Update Status'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="select-status-label">{t('common.status') || 'Status'}</InputLabel>
              <Select
                labelId="select-status-label"
                value={actionType}
                label={t('common.status') || 'Status'}
                onChange={(e) => setActionType(e.target.value)}
              >
                <MenuItem value="Approved">{t('providerClaims.btnApprove') || 'Approve'}</MenuItem>
                <MenuItem value="Rejected">{t('providerClaims.btnReject') || 'Reject'}</MenuItem>
              </Select>
            </FormControl>

            {claimToProcess && (
              <Box p={1.5} bgcolor="#f9fafb" borderRadius={2} border="1px dashed #e5e7eb">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Contact: {claimToProcess.contactTitle}
                </Typography>
                <Typography variant="body2">
                  Claimant: {claimToProcess.fullName} ({claimToProcess.role})
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Email: {claimToProcess.businessEmail}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={isSubmitting}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleUpdateClaimStatus}
            variant="contained"
            disabled={isSubmitting || !actionType}
            startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <Check />}
            sx={{
              background: 'linear-gradient(135deg, #0F3D3E 0%, #0a2829 100%)',
              color: '#ffffff',
              boxShadow: '0 2px 4px rgba(124, 58, 237, 0.2)',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              }
            }}
          >
            {t('common.update') || 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Proof Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            position: 'relative',
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            margin: 0,
            width: 'auto',
            maxWidth: 'none'
          }
        }}
        BackdropProps={{
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.9)' }
        }}
      >
        <IconButton
          aria-label="close"
          onClick={handlePreviewClose}
          sx={{
            position: 'fixed',
            right: 20,
            top: 20,
            zIndex: 1500,
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
            }
          }}
        >
          <Close fontSize="large" />
        </IconButton>
        <DialogContent sx={{ p: 0, height: '100vh', width: '100vw', display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
          {previewUrl ? (
            previewUrl.toLowerCase().includes('.pdf') ? (
              <Box sx={{ width: '95vw', height: '95vh', p: 0, overflow: 'hidden', backgroundColor: '#fff', borderRadius: '4px' }}>
                <iframe
                  src={previewUrl}
                  title="Proof Preview"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              </Box>
            ) : (
              <Box
                component="img"
                src={previewUrl}
                alt="Proof Preview"
                sx={{
                  maxWidth: '95vw',
                  maxHeight: '95vh',
                  objectFit: 'contain'
                }}
              />
            )
          ) : (
            <Box sx={{ p: 10, textAlign: 'center' }}>
              <Typography color="white">No document preview available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Toast Alert */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={4000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClaimProvider;
